import React from 'react';
import { motion } from 'framer-motion';
import { Target, Users, Globe, Lightbulb, Heart, Zap } from 'lucide-react';

function MissionPage() {
  const missionPoints = [
    {
      icon: Globe,
      title: 'Global Accessibility',
      description: 'Making premium education accessible to learners worldwide, breaking down geographical and financial barriers.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Users,
      title: 'Community Building',
      description: 'Creating supportive learning communities where students can grow, collaborate, and achieve their goals together.',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Lightbulb,
      title: 'Innovation in Learning',
      description: 'Continuously evolving our teaching methods and content to stay at the forefront of educational technology.',
      color: 'from-yellow-500 to-orange-500'
    },
    {
      icon: Heart,
      title: 'Student Success',
      description: 'Putting student success at the center of everything we do, ensuring every learner achieves their potential.',
      color: 'from-pink-500 to-rose-500'
    },
    {
      icon: Zap,
      title: 'Practical Skills',
      description: 'Focusing on practical, industry-relevant skills that prepare students for real-world challenges and opportunities.',
      color: 'from-purple-500 to-violet-500'
    },
    {
      icon: Target,
      title: 'Excellence',
      description: 'Maintaining the highest standards in course quality, instructor expertise, and learning outcomes.',
      color: 'from-indigo-500 to-blue-500'
    }
  ];

  const impactStats = [
    { number: '0', label: 'Lives Transformed', description: 'Students who have achieved their career goals' },
    { number: 'New', label: 'Platform Status', description: 'We are just getting started' },
    { number: '1', label: 'Country Reached', description: 'Starting locally, expanding globally' },
    { number: '1', label: 'Expert Instructor', description: 'Industry professional sharing knowledge' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-50 to-blue-50 py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl font-bold text-slate-900 mb-6">
              Our Mission
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed mb-8">
              To democratize premium education and empower learners worldwide to achieve their dreams 
              through expert-led courses, innovative learning experiences, and a supportive community.
            </p>
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Our Core Mission Statement</h2>
              <p className="text-lg text-slate-700 leading-relaxed italic">
                "We believe that everyone deserves access to world-class education. Our mission is to break down 
                barriers, foster innovation in learning, and create opportunities for personal and professional 
                growth that transform lives and communities worldwide."
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission Points */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-slate-900 mb-6">What Drives Us</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              These six pillars form the foundation of our mission and guide every decision we make.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {missionPoints.map((point, index) => (
              <motion.div
                key={point.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group"
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-100">
                  <div className={`w-16 h-16 bg-gradient-to-r ${point.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <point.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">{point.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{point.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-slate-900 mb-6">Our Impact</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Numbers tell a story, but behind each statistic is a real person whose life has been transformed.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {impactStats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h3 className="text-4xl font-bold text-blue-600 mb-2">{stat.number}</h3>
                  <h4 className="text-xl font-semibold text-slate-900 mb-2">{stat.label}</h4>
                  <p className="text-slate-600 text-sm">{stat.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-slate-900 mb-6">Our Vision for the Future</h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                We envision a world where education is not a privilege but a fundamental right, 
                where anyone can access the knowledge and skills they need to thrive.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl p-8 text-white"
              >
                <h3 className="text-2xl font-bold mb-6">Short-term Goals (2025)</h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-white rounded-full mt-2 mr-3"></div>
                    <span>Build our first community of learners</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-white rounded-full mt-2 mr-3"></div>
                    <span>Launch additional courses in emerging technologies</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-white rounded-full mt-2 mr-3"></div>
                    <span>Establish partnerships with universities</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-white rounded-full mt-2 mr-3"></div>
                    <span>Implement AI-powered personalized learning paths</span>
                  </li>
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-green-500 to-teal-600 rounded-2xl p-8 text-white"
              >
                <h3 className="text-2xl font-bold mb-6">Long-term Vision (2030)</h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-white rounded-full mt-2 mr-3"></div>
                    <span>Serve thousands of learners globally</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-white rounded-full mt-2 mr-3"></div>
                    <span>Create a comprehensive learning ecosystem</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-white rounded-full mt-2 mr-3"></div>
                    <span>Develop VR/AR immersive learning experiences</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-white rounded-full mt-2 mr-3"></div>
                    <span>Establish scholarship programs for underserved communities</span>
                  </li>
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-blue-600">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              Join Our Mission
            </h2>
            <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
              Be part of the movement that's transforming education worldwide. 
              Be among the first to join our new learning platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="/courses"
                className="inline-block bg-white text-purple-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-purple-50 transition-colors duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Start Learning Today
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default MissionPage;
